import { setTheme, setCompleteTheme, setHighContrast, toggleTheme } from '@core/state/actions';
import { selectTheme, selectCompleteTheme, selectIsDarkTheme, selectIsHighContrast } from '@core/state/selectors';
import type { Theme } from '@soteria/design-system-ve';
import { useAppDispatch, useAppSelector } from '@store';
import { useCallback } from 'react';

/**
 * Custom hook for managing the theme
 * @returns Object with theme state and methods
 */
export function useTheme() {
  const dispatch = useAppDispatch();

  // Use the memoized selectors
  const theme = useAppSelector(selectTheme);
  const completeTheme = useAppSelector(selectCompleteTheme);
  const isDarkTheme = useAppSelector(selectIsDarkTheme);
  const isHighContrast = useAppSelector(selectIsHighContrast);

  // Set the theme (light, dark, or system)
  const setThemeValue = useCallback(
    (newTheme: 'light' | 'dark' | 'system') => {
      dispatch(setTheme(newTheme));
    },
    [dispatch]
  );

  // Set the complete theme object
  const setCompleteThemeValue = useCallback(
    (newTheme: Theme) => {
      dispatch(setCompleteTheme(newTheme));
    },
    [dispatch]
  );

  // Toggle between light and dark themes
  const toggleThemeValue = useCallback(() => {
    dispatch(toggleTheme());
  }, [dispatch]);

  // Set high contrast mode
  const setHighContrastValue = useCallback(
    (enabled: boolean) => {
      dispatch(setHighContrast(enabled));
    },
    [dispatch]
  );

  return {
    theme,
    completeTheme,
    isDarkTheme,
    isHighContrast,
    setTheme: setThemeValue,
    setCompleteTheme: setCompleteThemeValue,
    setHighContrast: setHighContrastValue,
    toggleTheme: toggleThemeValue,
  };
}
